import { arrayToTree } from 'performant-array-to-tree'
import { useCallback, useMemo } from 'react'
import {
  type TaxonomyNode,
  type TaxonomyNodeTreeItem,
} from 'api/taxonomy-nodes/taxonomyNodes.types'
import { useGetTaxonomyNodesQuery } from '__codegen__/api'

interface TaxonomyNodesById {
  [id: string]: TaxonomyNode
}

const ROOT_NODE_ID = 1

export const useTaxonomyNodes = () => {
  const { loading, error, data } = useGetTaxonomyNodesQuery({
    fetchPolicy: 'cache-first',
  })

  /**
   * Flattened array of taxonomy nodes.
   */
  const taxonomyNodes = useMemo<TaxonomyNode[]>(() => {
    if (data) {
      return data.taxonomyNodes.map(node => ({
        id: +node.id,
        name: node.name,
        parentId: +node.parentId,
      }))
    }
  }, [data])

  /**
   * Hash of taxonomy nodes by id.
   */
  const taxonomyNodesById = useMemo<TaxonomyNodesById>(() => {
    if (taxonomyNodes) {
      return taxonomyNodes.reduce((byId, node) => ((byId[node.id] = node), byId), {})
    }
  }, [taxonomyNodes])

  /**
   * Nested array of taxonomy nodes.
   * Intended for use with react-simple-tree-menu to display the taxonomy node tree.
   */
  const taxonomyNodeTree = useMemo<TaxonomyNodeTreeItem[]>(() => {
    if (taxonomyNodes) {
      const mappedTaxonomyNodes = taxonomyNodes.map(node => ({
        key: node.id.toString(),
        parentId: node.parentId === 1 ? null : node.parentId,
        label: node.name,
      }))
      const tree = arrayToTree(mappedTaxonomyNodes, {
        childrenField: 'nodes',
        dataField: null,
        id: 'key',
      }) as TaxonomyNodeTreeItem[]

      return tree
    }
  }, [taxonomyNodes])

  /**
   * Returns the taxonomy node for a given id.
   */
  const getTaxonomyNode = useCallback(
    (taxonomyNodeId: number) => {
      if (taxonomyNodeId in taxonomyNodesById) {
        return taxonomyNodesById[taxonomyNodeId]
      }
    },
    [taxonomyNodesById]
  )

  /**
   * Returns the taxonomy node name for a given id.
   */
  const getTaxonomyNodeName = useCallback(
    (taxonomyNodeId: number) => {
      if (taxonomyNodeId in taxonomyNodesById) {
        return taxonomyNodesById[taxonomyNodeId].name
      }
    },
    [taxonomyNodesById]
  )

  /**
   * Returns taxonomy node for a given id and all of its ancestors.
   */
  const getTaxonomyNodePath: (taxonomyNodeId: number) => TaxonomyNode[] = useCallback(
    taxonomyNodeId => {
      if (taxonomyNodeId in taxonomyNodesById) {
        const taxonomyNode = taxonomyNodesById[taxonomyNodeId]

        if (taxonomyNode.parentId === ROOT_NODE_ID) {
          return [taxonomyNode]
        } else {
          const ancestors = getTaxonomyNodePath(taxonomyNode.parentId)
          ancestors.push(taxonomyNode)
          return ancestors
        }
      }
    },
    [taxonomyNodesById]
  )

  return {
    taxonomyNodes,
    taxonomyNodeTree,
    getTaxonomyNode,
    getTaxonomyNodeName,
    getTaxonomyNodePath,
    apiLoading: loading,
    apiError: error,
  }
}
